import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getBaseUrl } from "@/lib/site";
import { isNativeApp } from "@/lib/platform";

export default function ForgotPassword() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const isNative = isNativeApp();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    try {
      const redirectTo = `${getBaseUrl()}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (error) throw error;

      setSent(true);
      toast({
        title: "Check your email",
        description: "If an account exists for that email, you’ll receive a password reset link.",
      });
    } catch (err: any) {
      toast({
        title: "Couldn’t send reset email",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />

      <main
        className={`public-auth-theme flex-1 flex justify-center bg-gradient-hero px-4 ${
          isNative ? "items-start pt-28" : "items-center pt-16"
        }`}
      >
        <div className={`w-full max-w-md mx-auto ${isNative ? "pb-6" : "py-6 sm:py-12"}`}>
          <div className={`text-center ${isNative ? "mb-4" : "mb-6 sm:mb-8"}`}>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Reset your password</h1>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Enter your email and we’ll send you a link to reset your password.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-soft-lg p-5 sm:p-8">
            {sent ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  If your email is registered, you’ll receive a reset link shortly. Please check your inbox and spam folder.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="gradient" className="w-full">
                    <Link to="/signin">Back to sign in</Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSent(false);
                    }}
                  >
                    Send again
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    autoComplete="email"
                    required
                  />
                </div>

                <Button type="submit" variant="gradient" className="w-full h-11" disabled={isLoading || !email.trim()}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  Remembered it?{" "}
                  <Link
                    to="/signin"
                    className="bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-medium hover:underline animate-gradient-subtle"
                  >
                    Sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      {!isNative && <PublicFooter />}
    </div>
  );
}

